import { useCallback, useState, useEffect } from 'react';
import liff from '@line/liff';

// redux
import { useDispatch } from 'react-redux';
import { updateUserId } from '../store/user';

// type
type QueryStatus = {
  response: string;
  loading: boolean;
  error: string;
};

interface GTagWindow extends Window {
  gtagEvent: (eventName: string, eventProps?: { [key: string]: string | number }) => void;
}
declare const window: GTagWindow;

// Liffアプリ初期化
export const useAuthLiff = (): QueryStatus => {
  const dispatch = useDispatch();

  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // LIFFにログインする
  const liffId = process.env.REACT_APP_LIFF_ID as string;
  const actLoginLiff = useCallback(() => {
    (async () => {
      if (!liffId) {
        setError('LIFF IDが設定されていません');
        return;
      }

      try {
        setLoading(true); // 処理開始

        await liff.init({ liffId });

        // LIFFブラウザで起動していない場合はLINEログインする
        if (!liff.isInClient() && !liff.isLoggedIn()) {
          liff.login({});
          return;
        }

        // LINE ユーザIDをstoreへ保存
        const decodeIdToken = liff.getDecodedIDToken();
        if (!decodeIdToken) {
          throw new Error('IDトークンの取得に失敗しました');
        }

        const userId = decodeIdToken.sub;
        if (!userId) {
          throw new Error('ユーザIDの取得に失敗しました');
        }

        dispatch(updateUserId(userId));
        setResponse(userId);
      } catch (err: unknown) {
        let errorMessage = 'LIFF初期化に失敗しました';
        if (err instanceof Error) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        // GA：LIFFログインに失敗
        window.gtagEvent('error_LIFF_init', {
          method: 'login',
          error: errorMessage,
        });
        console.error('LIFF initialize failed:', errorMessage);
      } finally {
        setLoading(false); // 処理終了
      }
    })();
  }, [dispatch, liffId]);

  // ページを表示したとき
  useEffect(() => {
    if (!response && !loading) {
      actLoginLiff();
    }
  }, [response, loading, actLoginLiff]);

  // 戻り値
  return { response, loading, error };
};
